import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, OnInit, Output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ComplexArticleBlueprintDTO } from '@admintool/article/generate-article/complex-article-blueprints/complex-article-blueprint';
import { ComplexArticleBlueprintRulesComponent } from '@admintool/article/generate-article/complex-article-blueprints/complex-article-blueprint-rules/complex-article-blueprint-rules.component';
import { ComplexArticleBlueprintDataService } from '@admintool/article/generate-article/complex-article-blueprints/services/complex-article-blueprint.data.service';
import { ComplexArticleBlueprintService, ComplexArticleBlueprintValidation } from '@admintool/article/generate-article/complex-article-blueprints/services/complex-article-blueprint.service';
import { ArticleDataService } from '@article/service/article-data.service';
import { ProjectNavigationPresenter } from '@project-navigation/project-navigation.presenter';
import { ArticleProjectGroupDataService } from '@project/components/article/manager/article-project-group-data.service';
import { SaveAsCompositeMode } from '@project/components/project/project-save/save-project.service';
import { ModalComponent } from '@shared/modal/modal.component';
import { SvgIconComponent } from '@shared/svg-icon/svg-icon.component';
import { ArrayUtils } from '@shared/util/array-utils';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-complex-article-blueprint-properties',
  imports: [
    FormsModule,
    TranslateModule,
    SvgIconComponent,
    ComplexArticleBlueprintRulesComponent,
    ModalComponent,
  ],
  templateUrl: './complex-article-blueprint-properties.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ComplexArticleBlueprintPropertiesComponent implements OnInit {
  private readonly projectNavigationPresenter = inject(ProjectNavigationPresenter);
  private readonly complexArticleBlueprintService = inject(ComplexArticleBlueprintService);
  private readonly complexArticleBlueprintDataService = inject(ComplexArticleBlueprintDataService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly articleProjectGroupDataService = inject(ArticleProjectGroupDataService);

  public blueprint!: ComplexArticleBlueprintDTO;
  public projectGroupName: string | undefined = undefined;
  @Output() public finish = new EventEmitter<ComplexArticleBlueprintDTO | undefined>();

  protected readonly SaveAsCompositeMode = SaveAsCompositeMode;
  public allArticleTypes = toSignal(inject(ArticleDataService).getAllArticleTypes());

  public validationErrors = signal<ComplexArticleBlueprintValidation>({
    valid: true,
    errorsText: undefined,
  });


  public ngOnInit(): void {
    this.reValidate();
    this.updateProjectGroupName();
  }

  public async selectProjectGroup(): Promise<void> {
    const projectGroup = await this.projectNavigationPresenter.openSelectProjectGroupDialog(true);
    this.blueprint.projectGroupId = projectGroup?.id ?? -1;
    await this.updateProjectGroupName();
    this.reValidate();
  }

  private async updateProjectGroupName(): Promise<void> {
    if(this.blueprint.projectGroupId == -1) return;
    const projectGroup = await firstValueFrom(this.articleProjectGroupDataService.loadArticleProjectGroupById(this.blueprint.projectGroupId));
    this.projectGroupName = projectGroup?.groupName ?? 'invalid';
    this.cdRef.markForCheck();
  }

  protected async save(): Promise<void> {
    await firstValueFrom(this.complexArticleBlueprintDataService.save(this.blueprint));
    this.finish.emit(this.blueprint);
  }

  protected cancel(): void {
    this.finish.emit(undefined);
  }

  protected reValidate(): void {
    this.validationErrors.set(this.complexArticleBlueprintService.validateBlueprint(this.blueprint));
    this.cdRef.markForCheck();
  }

  public updateAdditional(event: Event): void {
    this.blueprint.articleNumberSchema.additional = ArrayUtils.numberArrayFromString((event.target as HTMLInputElement).value, ';');
  }

  public updateExcluded(event: Event): void {
    this.blueprint.articleNumberSchema.excluded = ArrayUtils.numberArrayFromString((event.target as HTMLInputElement).value, ';');
  }
}
